const nativeLocks = new WeakMap<Element, { count: number; styles: { name: string; value: string; priority: string }[] }>();

function isScrollAlreadyBlocked(element: Element): boolean {
	const document = element.ownerDocument;
	const view = document.defaultView;
	if (!view) return false;
	const blocksBothAxes = (target: Element) => {
		const style = view.getComputedStyle(target);
		return ['hidden', 'clip'].includes(style.overflowX) && ['hidden', 'clip'].includes(style.overflowY);
	};
	if (blocksBothAxes(element)) return true;
	// PrimeVue blocks the viewport through body. Adding overflow to html as well
	// changes the scrolling element with height: 100% and resets the background.
	return element === document.scrollingElement && !!document.body && blocksBothAxes(document.body);
}

function lockNativeScrollbar(element: Element): () => void {
	const existing = nativeLocks.get(element);
	const style = (element as HTMLElement).style;
	if (existing) existing.count++;
	else {
		const styles = ['overflow-x', 'overflow-y', 'scrollbar-gutter'].map(name => ({
			name, value: style.getPropertyValue(name), priority: style.getPropertyPriority(name),
		}));
		nativeLocks.set(element, { count: 1, styles });
		// Preserve the space of an existing scrollbar without changing body layout.
		const document = element.ownerDocument;
		const computed = document.defaultView?.getComputedStyle(element);
		const gutter = element === document.scrollingElement
			? (document.defaultView?.innerWidth ?? 0) - document.documentElement.clientWidth
			: (element as HTMLElement).offsetWidth - element.clientWidth - parseFloat(computed?.borderLeftWidth ?? '0') - parseFloat(computed?.borderRightWidth ?? '0');
		if (gutter > 0 && computed?.scrollbarGutter === 'auto') style.setProperty('scrollbar-gutter', 'stable');
		style.setProperty('overflow', 'hidden', 'important');
	}
	return () => {
		const lock = nativeLocks.get(element);
		if (!lock || --lock.count > 0) return;
		for (const { name, value, priority } of lock.styles) {
			if (value) style.setProperty(name, value, priority);
			else style.removeProperty(name);
		}
		nativeLocks.delete(element);
	};
}

/** Disable native scrollbars as well as wheel/touch input, preserving all offsets. */
export function lockEditorScroll(anchor: HTMLElement, allowedScrollSelector = '.ew-popover, .p-dropdown-panel, .p-datepicker, .p-calendar-panel'): () => void {
	const documents = new Set<Document>();
	const targets = new Set<Element>();
	let element: Element | null = anchor;
	while (element) {
		const document: Document = element.ownerDocument;
		documents.add(document);
		if (document.scrollingElement) targets.add(document.scrollingElement);
		targets.add(element);
		if (element.parentElement) {
			element = element.parentElement;
		} else {
			const root = element.getRootNode();
			if ('host' in root) element = (root as ShadowRoot).host;
			else {
				// Focus in an embedded editor can also scroll its containing page.
				try { element = document.defaultView?.frameElement ?? null; }
				catch { element = null; } // Cross-origin parents are inaccessible.
			}
		}
	}
	const positions = [...targets].map(element => ({ element, top: element.scrollTop, left: element.scrollLeft }));
	const releaseNativeLocks = [...targets]
		.filter(element => {
			if (nativeLocks.has(element)) return true;
			if (isScrollAlreadyBlocked(element)) return false;
			return element === element.ownerDocument.scrollingElement || (
				(element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) &&
				/(auto|scroll)/.test(element.ownerDocument.defaultView?.getComputedStyle(element).overflow ?? '')
			);
		})
		.map(lockNativeScrollbar);
	const asElement = (target: EventTarget | null): Element | null =>
		target && 'nodeType' in target && target.nodeType === 1 ? target as Element : null;
	const insideEditor = (target: EventTarget | null) => !!asElement(target)?.closest(allowedScrollSelector);

	function preventBackgroundScroll(event: Event) {
		if (!insideEditor(event.target) && event.cancelable) event.preventDefault();
	}
	function onWheel(event: WheelEvent) {
		if (!event.ctrlKey) preventBackgroundScroll(event);
	}
	function onKeyDown(event: KeyboardEvent) {
		if (insideEditor(event.target)) return;
		if (asElement(event.target)?.closest('input, textarea, select, [contenteditable="true"]')) return;
		if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) event.preventDefault();
	}
	function restorePositions() {
		for (const { element, top, left } of positions) {
			if (element.scrollTop !== top || element.scrollLeft !== left) element.scrollTo({ top, left, behavior: 'instant' });
		}
	}
	function onScroll(event: Event) {
		if (!insideEditor(event.target)) restorePositions();
	}
	// Apply/restoring overflow must not change the saved page/container positions.
	restorePositions();

	for (const document of documents) {
		document.addEventListener('wheel', onWheel, { capture: true, passive: false });
		document.addEventListener('touchmove', preventBackgroundScroll, { capture: true, passive: false });
		document.addEventListener('keydown', onKeyDown, true);
		document.addEventListener('scroll', onScroll, true);
	}
	// Scroll events inside shadow roots do not reach the document.
	for (const target of targets) target.addEventListener('scroll', onScroll);
	let released = false;
	const release = () => {
		if (released) return;
		released = true;
		anchor.ownerDocument.defaultView?.removeEventListener('pagehide', release);
		for (const document of documents) {
			document.removeEventListener('wheel', onWheel, true);
			document.removeEventListener('touchmove', preventBackgroundScroll, true);
			document.removeEventListener('keydown', onKeyDown, true);
			document.removeEventListener('scroll', onScroll, true);
		}
		for (const target of targets) target.removeEventListener('scroll', onScroll);
		for (const release of releaseNativeLocks.reverse()) release();
		restorePositions();
	};
	// Removing the details iframe does not unmount its Vue application.
	// Release listeners in the parent document when the frame is closed.
	anchor.ownerDocument.defaultView?.addEventListener('pagehide', release, { once: true });
	return release;
}
